import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Alert, Card, CardContent,
  Fade, Chip, Button, TextField,
} from '@mui/material';
import { CalendarCheck, Clock, DollarSign, XCircle } from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const StatCard = styled(Card)(() => ({
  background: '#141926',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
}));

const StyledTable = styled(TableContainer)(() => ({
  background: '#141926',
  borderRadius: 16,
  border: '1px solid rgba(255,255,255,0.06)',
  overflowX: 'auto',
  '& .MuiTableCell-root': {
    borderBottom: '1px solid rgba(124, 58, 237, 0.1)',
    color: '#fff',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    color: '#7C3AED',
    fontWeight: 600,
  },
}));

const STATUS_LABELS = {
  paid: { label: 'Pago', color: '#10B981', bg: 'rgba(16,185,129,0.15)' },
  pending_payment: { label: 'Aguardando pgto', color: '#F59E0B', bg: 'rgba(245,158,11,0.15)' },
  expired: { label: 'Expirado', color: '#6B7280', bg: 'rgba(107,114,128,0.15)' },
  cancelled: { label: 'Cancelado', color: '#EF4444', bg: 'rgba(239,68,68,0.15)' },
};

const AdminAgendamentos = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookings, setBookings] = useState([]);
  const [statusFilter, setStatusFilter] = useState('paid');

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchBookings = async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/bm-booking/admin/all`, authHeader());
      if (res.data.success) setBookings(res.data.bookings || []);
      else setError('Erro ao carregar agendamentos');
    } catch { setError('Erro ao carregar agendamentos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBookings(); }, []);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancelar este agendamento? O horário voltará a ficar disponível.')) return;
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/bm-booking/admin/${id}/cancel`, {}, authHeader());
      fetchBookings();
    } catch { setError('Erro ao cancelar'); }
  };

  const stats = useMemo(() => {
    const paid = bookings.filter(b => b.status === 'paid');
    const pending = bookings.filter(b => b.status === 'pending_payment');
    const faturamento = paid.reduce((s, b) => s + (b.valor || 0), 0);
    return { paidCount: paid.length, pendingCount: pending.length, faturamento };
  }, [bookings]);

  const filtered = useMemo(() => {
    const list = statusFilter ? bookings.filter(b => b.status === statusFilter) : bookings;
    return [...list].sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
  }, [bookings, statusFilter]);

  const fmtDate = (iso) => (iso ? iso.split('-').reverse().join('/') : '-');
  const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('pt-BR') : '-');

  return (
    <Fade in timeout={400}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <CalendarCheck size={24} color="#7C3AED" />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#F9FAFB' }}>Agendamentos de Verificação</Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Stats */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
          <StatCard>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.2, borderRadius: 2, background: 'rgba(16,185,129,0.15)' }}><CalendarCheck size={22} color="#10B981" /></Box>
              <Box>
                <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>Reservas pagas</Typography>
                <Typography sx={{ color: '#F9FAFB', fontWeight: 700, fontSize: '1.4rem' }}>{stats.paidCount}</Typography>
              </Box>
            </CardContent>
          </StatCard>
          <StatCard>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.2, borderRadius: 2, background: 'rgba(245,158,11,0.15)' }}><Clock size={22} color="#F59E0B" /></Box>
              <Box>
                <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>Aguardando pagamento</Typography>
                <Typography sx={{ color: '#F9FAFB', fontWeight: 700, fontSize: '1.4rem' }}>{stats.pendingCount}</Typography>
              </Box>
            </CardContent>
          </StatCard>
          <StatCard>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.2, borderRadius: 2, background: 'rgba(124,58,237,0.15)' }}><DollarSign size={22} color="#A78BFA" /></Box>
              <Box>
                <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>Faturamento (pago)</Typography>
                <Typography sx={{ color: '#F9FAFB', fontWeight: 700, fontSize: '1.4rem' }}>R$ {stats.faturamento.toFixed(2).replace('.', ',')}</Typography>
              </Box>
            </CardContent>
          </StatCard>
        </Box>

        {/* Filtro */}
        <Box sx={{ mb: 2, maxWidth: 260 }}>
          <TextField select fullWidth size="small" label="Status" value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ background: '#141926', borderRadius: 2 }}>
            <option value="">Todos</option>
            <option value="paid">Pagos (agenda)</option>
            <option value="pending_payment">Aguardando pagamento</option>
            <option value="expired">Expirados</option>
            <option value="cancelled">Cancelados</option>
          </TextField>
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress sx={{ color: '#7C3AED' }} /></Box>
        ) : filtered.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', background: '#141926', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4 }}>
            <Typography sx={{ color: '#6B7280' }}>Nenhum agendamento encontrado.</Typography>
          </Paper>
        ) : (
          <StyledTable component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Horário</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>WhatsApp</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Método</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Pago em</TableCell>
                  <TableCell>Obs.</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(b => {
                  const st = STATUS_LABELS[b.status] || { label: b.status, color: '#9CA3AF', bg: 'rgba(255,255,255,0.06)' };
                  return (
                    <TableRow key={b._id}>
                      <TableCell>{fmtDate(b.date)}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#A78BFA !important' }}>{b.time}</TableCell>
                      <TableCell>{b.nome || '-'}</TableCell>
                      <TableCell>{b.whatsapp || '-'}</TableCell>
                      <TableCell>{b.email || '-'}</TableCell>
                      <TableCell>{b.method === 'anydesk' ? 'AnyDesk' : 'Convite'}</TableCell>
                      <TableCell>R$ {(b.valor || 0).toFixed(2).replace('.', ',')}</TableCell>
                      <TableCell>
                        <Chip label={st.label} size="small" sx={{ background: st.bg, color: st.color, fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>{fmtDateTime(b.paidAt)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'normal !important', maxWidth: 200 }}>{b.observacao || '-'}</TableCell>
                      <TableCell>
                        {(b.status === 'paid' || b.status === 'pending_payment') && (
                          <Button size="small" startIcon={<XCircle size={14} />} onClick={() => handleCancel(b._id)}
                            sx={{ textTransform: 'none', color: '#EF4444', minWidth: 0 }}>
                            Cancelar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </StyledTable>
        )}
      </Box>
    </Fade>
  );
};

export default AdminAgendamentos;
